import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";

// Eagerly loaded: LoginPage (initial paint) only.
import LoginPage from "./pages/LoginPage";

// All other pages are code-split via React.lazy so the initial JS bundle
// stays small. Webpack splits each into its own chunk fetched on demand.
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const LeadProfilePage = lazy(() => import("./pages/LeadProfilePage"));
const ImportLeadsPage = lazy(() => import("./pages/ImportLeadsPage"));
const WalkInRegistrationPage = lazy(() => import("./pages/WalkInRegistrationPage"));
const NewApplicationPage = lazy(() => import("./pages/NewApplicationPage"));
const RemindersPage = lazy(() => import("./pages/RemindersPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const CampaignsPage = lazy(() => import("./pages/CampaignsPage"));
const CampaignViewPage = lazy(() => import("./pages/CampaignViewPage"));
const CampaignFormBuilderPage = lazy(() => import("./pages/CampaignFormBuilderPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const InvalidPhonesPage = lazy(() => import("./pages/InvalidPhonesPage"));
const AgentProfilesPage = lazy(() => import("./pages/AgentProfilesPage"));
const AgentProfileViewPage = lazy(() => import("./pages/AgentProfileViewPage"));
const PublicFormPage = lazy(() => import("./pages/PublicFormPage"));
const SasurieApplicationPage = lazy(() => import("./pages/SasurieApplicationPage"));
const VisitorApplicationPage = lazy(() => import("./pages/VisitorApplicationPage"));
const ApplicationsPage = lazy(() => import("./pages/ApplicationsPage"));
const CTAFormPage = lazy(() => import("./pages/CTAFormPage"));
// Master Pages
const ProgrammeMasterPage = lazy(() => import("./pages/ProgrammeMasterPage"));
const FeeStructurePage = lazy(() => import("./pages/FeeStructurePage"));
// Report Pages
const WalkInReportPage = lazy(() => import("./pages/WalkInReportPage"));
const LeadsByStageReportPage = lazy(() => import("./pages/LeadsByStageReportPage"));
const LeadsByPreferenceReportPage = lazy(() => import("./pages/LeadsByPreferenceReportPage"));
const ApplicationReportPage = lazy(() => import("./pages/ApplicationReportPage"));
const DailyCollectionReportPage = lazy(() => import("./pages/DailyCollectionReportPage"));
// Visitor Pages
const VisitorEntryPage = lazy(() => import("./pages/VisitorEntryPage"));
const VisitorDashboardPage = lazy(() => import("./pages/VisitorDashboardPage"));
// Role-specific Dashboards
const CounsellorDashboard = lazy(() => import("./pages/CounsellorDashboard"));
const DiscountApprovalsPage = lazy(() => import("./pages/DiscountApprovalsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
// Receptionist Pages
const ReceptionistPendingPage = lazy(() => import("./pages/ReceptionistPendingPage"));
// Analytics Dashboards
const WalkinDashboardPage = lazy(() => import("./pages/WalkinDashboardPage"));
const ManagementViewPage = lazy(() => import("./pages/ManagementViewPage"));
const CounsellorViewPage = lazy(() => import("./pages/CounsellorViewPage"));
const BillingDashboardPage = lazy(() => import("./pages/BillingDashboardPage"));

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (roles && !roles.includes(user?.role)) {
        // Redirect reception_desk to walk-in page instead of dashboard
        if (user?.role === 'reception_desk') {
            return <Navigate to="/walk-in" replace />;
        }
        // Redirect billing_agent to billing page
        if (user?.role === 'billing_agent') {
            return <Navigate to="/billing" replace />;
        }
        if (user?.role === 'billing_manager') {
            return <Navigate to="/billing-dashboard" replace />;
        }
        if (user?.role === 'billing_admin') {
            return <Navigate to="/billing-dashboard" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Redirect authenticated users from login
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (isAuthenticated) {
        // Redirect reception_desk to walk-in page instead of dashboard
        if (user?.role === 'reception_desk') {
            return <Navigate to="/walk-in" replace />;
        }
        // Redirect billing_agent to billing page
        if (user?.role === 'billing_agent') {
            return <Navigate to="/billing" replace />;
        }
        if (user?.role === 'billing_manager') {
            return <Navigate to="/billing-dashboard" replace />;
        }
        if (user?.role === 'billing_admin') {
            return <Navigate to="/billing-dashboard" replace />;
        }
        // Redirect admin and manager to analytics page
        if (user?.role === 'admin' || user?.role === 'manager') {
            return <Navigate to="/analytics" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            
            {/* Public Form Route (No Auth Required) */}
            <Route path="/form/:campaignId" element={<PublicFormPage />} />
            
            {/* Campaign CTA Form (Public - Creates leads with Opportunity status) */}
            <Route path="/cta/:campaignId" element={<CTAFormPage />} />
            
            {/* Sasurie College Public Application Form */}
            <Route path="/apply/sasurie" element={<SasurieApplicationPage />} />
            <Route path="/apply" element={<SasurieApplicationPage />} />
            
            {/* Public Visitor Application Form (Continue from Visitor ID) */}
            <Route path="/visitor-application" element={<VisitorApplicationPage />} />
            
            {/* Public Visitor Entry Form (No Auth Required) */}
            <Route path="/visitor-entry" element={<VisitorEntryPage />} />

            {/* Public Management Dashboard (No Auth Required) */}
            <Route path="/management-view" element={<ManagementViewPage />} />
            <Route path="/counsellor-view" element={<CounsellorViewPage />} />

            {/* Protected Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/counsellor-dashboard"
                element={
                    <ProtectedRoute roles={['counsellor', 'admin']}>
                        <CounsellorDashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/receptionist/pending"
                element={
                    <ProtectedRoute roles={['receptionist', 'reception_desk', 'admin', 'manager']}>
                        <ReceptionistPendingPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/walkin-dashboard"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'management']}>
                        <WalkinDashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/analytics"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'management', 'secretary', 'chairman']}>
                        <AnalyticsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/leads"
                element={
                    <ProtectedRoute>
                        <LeadsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/applications"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'counsellor', 'secretary']}>
                        <ApplicationsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/leads/new-application"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'agent']}>
                        <NewApplicationPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/leads/:id"
                element={
                    <ProtectedRoute>
                        <LeadProfilePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/leads/import"
                element={
                    <ProtectedRoute roles={['admin', 'manager']}>
                        <ImportLeadsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/walk-in"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'agent', 'reception_desk']}>
                        <WalkInRegistrationPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reminders"
                element={
                    <ProtectedRoute roles={['agent', 'counsellor']}>
                        <RemindersPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/agents"
                element={
                    <ProtectedRoute roles={['admin', 'manager']}>
                        <AgentProfilesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/agents/:id"
                element={
                    <ProtectedRoute roles={['admin', 'manager']}>
                        <AgentProfileViewPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/users"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <UsersPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/masters/programmes"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <ProgrammeMasterPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/masters/fee-structure"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <FeeStructurePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/campaigns"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <CampaignsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/campaigns/:id"
                element={
                    <ProtectedRoute roles={['admin', 'manager']}>
                        <CampaignViewPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/campaigns/:id/form-builder"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <CampaignFormBuilderPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <SettingsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/invalid-phones"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'management']}>
                        <InvalidPhonesPage />
                    </ProtectedRoute>
                }
            />

            {/* Report Routes */}
            <Route
                path="/reports/walk-ins"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'reception_desk', 'billing_admin']}>
                        <WalkInReportPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reports/leads-by-stage"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'billing_admin']}>
                        <LeadsByStageReportPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reports/leads-by-preference"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'billing_admin']}>
                        <LeadsByPreferenceReportPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reports/applications"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'management', 'billing_admin']}>
                        <ApplicationReportPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reports/daily-collection"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'management', 'billing_agent', 'billing_manager', 'billing_admin']}>
                        <DailyCollectionReportPage />
                    </ProtectedRoute>
                }
            />
            
            {/* Visitor Management (Protected) */}
            <Route
                path="/visitors"
                element={
                    <ProtectedRoute roles={['admin', 'manager', 'reception_desk']}>
                        <VisitorDashboardPage />
                    </ProtectedRoute>
                }
            />
            
            {/* Discount Approvals (Management) */}
            <Route
                path="/approvals"
                element={
                    <ProtectedRoute roles={['admin', 'management', 'manager']}>
                        <DiscountApprovalsPage />
                    </ProtectedRoute>
                }
            />
            
            {/* Billing Page (Billing Agent) */}
            <Route
                path="/billing"
                element={
                    <ProtectedRoute roles={['admin', 'billing_agent', 'billing_manager', 'billing_admin', 'manager']}>
                        <BillingPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/billing-dashboard"
                element={
                    <ProtectedRoute roles={['admin', 'billing_manager', 'billing_admin', 'manager']}>
                        <BillingDashboardPage />
                    </ProtectedRoute>
                }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Suspense
                    fallback={
                        <div className="min-h-screen flex items-center justify-center bg-background">
                            <div className="spinner" />
                        </div>
                    }
                >
                    <AppRoutes />
                </Suspense>
                <Toaster 
                    position="top-right" 
                    toastOptions={{
                        style: {
                            background: '#121214',
                            border: '1px solid #27272A',
                            color: '#FAFAFA'
                        }
                    }}
                />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
